import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaCheck, FaArrowRight, FaArrowLeft, FaPlus, FaSpinner, FaDownload, FaSearch } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Getcover.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const CERT_WEBHOOK = 'https://sms.definiteassurance.com/webhook/c924c7c4-d115-4724-a78c-27048b99b6f0';
const CERT_WEBHOOK_TEST = 'https://sms.definiteassurance.com/webhook/2c040572-1e1e-4f70-a6ff-ad5f1c432ed9';
const CERT_WEBHOOK_TEST_AUTH = { username: 'definite-uat', password: 'D3finit3@2026-#' };
// Flip to true to send certificate requests to the test webhook/payload instead of production.
const USE_TEST_CERT = false;
const PAYMENT_WEBHOOK = 'https://auto.definiteassurance.co.ke/webhook/0623c8f7-d286-45c4-b823-2675d25de35a';
const WALLET_WEBHOOK = 'https://sms.definiteassurance.com/webhook/04a58cac-a6b4-4bde-883a-b364f32d9ae9';
const DOUBLE_INS_WEBHOOK = 'https://sms.definiteassurance.com/webhook/d6c9608b-cd90-481a-995d-d40c908c96db';
const COMPANY = 'Definite';
const STAMP_DUTY = 40;
const DOUBLE_INS_DEBOUNCE_MS = 800;
const PAYMENT_TIMER_SECONDS = 60;
const PAYMENT_TIMER_RADIUS = 30;
const PAYMENT_TIMER_CIRCUMFERENCE = 2 * Math.PI * PAYMENT_TIMER_RADIUS;

// Product definitions
const PRODUCTS = [
  { code: 3617, name: 'Motor Private', img: '/private.jpg' },
  { code: 3582, name: 'Private Hire - Chauffeur Driven', img: '/chauffeur.jpg' },
  { code: 3583, name: 'Private Hire - Self Drive', img: '/self drive.jpg' },
  { code: 4694, name: 'Motor Private TOR', img: '/private.jpg' },
  { code: 4638, name: 'PSV MATATU (7-35)', img: '/psvmatatu.PNG' },
  { code: 3587, name: 'PSV BUS (36-105)', img: '/psvbus.jpg' },
  { code: 4652, name: 'PSV Boda Boda', img: '/boda.jpg' },
  { code: 4655, name: 'PSV Tuk Tuk', img: '/tuktukpsv.jpg' },
  { code: 4630, name: 'Motor Commercial', img: '/motorcom.jpg' },
  // { code: 3592, name: 'Motor Commercial Agricultural', img: '/agricultural.jpg' },
  { code: 3604, name: 'Tuk Tuk Commercial', img: '/tuktuk.jpg' },
  { code: 4695, name: 'Motor Commercial TOR', img: '/motorcom.jpg' },
  { code: 4653, name: 'Motorcycle Private', img: '/motorcycleprivate.jpg' },
];

const TONNAGE_PRODUCTS = [3531, 3588, 4688, 4695, 3604, 4630, 3592];
const LOW_SUM_PRODUCTS = [4652, 4653, 4655, 3604]; 

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatDateFromMs = (ms) => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (ms) => {
  const d = new Date(ms);
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const getNow = () => formatDateTime(Date.now());

const getDaysInCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
};

const validateKRAPin = (pin) => {
  const p = pin.toUpperCase();
  if (p.length !== 11) return { isValid: false, message: 'KRA PIN must be exactly 11 characters' };
  if (!/[A-Z]/.test(p[0]) || !/[A-Z]/.test(p[10])) return { isValid: false, message: 'Must start and end with a letter' };
  if (!/^[A-Z0-9]{9}$/.test(p.substring(1, 10))) return { isValid: false, message: 'Middle 9 characters must be alphanumeric' };
  return { isValid: true, message: 'Valid KRA PIN' };
};

const validateEmail = (email) => {
  const sanitized = email.replace(/[^a-zA-Z0-9@._-]/g, '');
  const valid = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(sanitized);
  return { isValid: valid, sanitized, message: valid ? 'Valid' : 'Invalid email format' };
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Getcover() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [dealerAccountCode, setDealerAccountCode] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [branch, setBranch] = useState(null);
  const [approved, setApproved] = useState(0);
  const [dealerCode, setDealerCode] = useState('');
  const [dsfApproved, setDsfApproved] = useState(0);
  const [parentIntermediary, setParentIntermediary] = useState(null);
  const [iraNumber, setIraNumber] = useState('');
  const [walletNo, setWalletNo] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [availableCommission, setAvailableCommission] = useState(0);
  const [hasBinder, setHasBinder] = useState(false);

  // ── Data ──
  const [clients, setClients] = useState([]);
  const [makemodel, setMakemodel] = useState(null);
  const [pcoveroptions, setPcoveroptions] = useState(null);
  const [models, setModels] = useState([]);
  // Timestamps (ms) of when each cached dataset was last synced from the API
  const [dataSyncedAt, setDataSyncedAt] = useState({ makemodel: null, pcoveroptions: null, clients: null });
  const [refreshingData, setRefreshingData] = useState(false);

  // ── Step / UI ──
  const [step, setStep] = useState(1);
  const [productCode, setProductCode] = useState('');
  const [productName, setProductName] = useState('');
  const [prdCode, setPrdCode] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [iraCode, setIraCode] = useState('');
  const [riskClass, setRiskClass] = useState('');

  // ── Cover options modal (step 1 → get quote) ──
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [coverOptions, setCoverOptions] = useState([]);
  const [coverOptionCode, setCoverOptionCode] = useState('');
  const [coverOption, setCoverOption] = useState(null);
  const [insuredItems, setInsuredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [capacity, setCapacity] = useState(0);
  const [sumInsured, setSumInsured] = useState(0);
  const [tonnage, setTonnage] = useState(0);
  const [coverPeriod, setCoverPeriod] = useState('');
  const [quoteText, setQuoteText] = useState('GET QUOTATION');
  
  // ── Additional benefits ──
  const [additionalBenefits, setAdditionalBenefits] = useState([]);
  const [selectedBenefits, setSelectedBenefits] = useState([]);
  const [benefitPremiumTotal, setBenefitPremiumTotal] = useState(0);

  // ── Quote results (step 2) ──
  const [premium, setPremium] = useState(null);
  const [premiumRate, setPremiumRate] = useState(null);
  const [basePremium, setBasePremium] = useState(null);
  const [PHCF, setPHCF] = useState(null);
  const [trainingLevy, setTrainingLevy] = useState(null);
  const [basicPremium, setBasicPremium] = useState(0);
  const [commission, setCommission] = useState('');
  const [netCommission, setNetCommission] = useState('');
  const [netPremium, setNetPremium] = useState('');
  const [payAmount, setPayAmount] = useState(0);

  // ── Risk details (step 3) ──
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [client, setClient] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientPin, setClientPin] = useState('');
  const [clientFullname, setClientFullname] = useState('');

  // Region/Branch (IntermediaryBranches) — only captured for agent 71693
  const [branchOptions, setBranchOptions] = useState([]); // [{OID, Region, BranchName}]
  const [selectedRegion, setSelectedRegion] = useState('');
  const [intermediaryBranch, setIntermediaryBranch] = useState(''); // IntermediaryBranches.OID

  const [cm, setCm] = useState('');
  const [commencementUnix, setCommencementUnix] = useState(null);
  const [commencementDate, setCommencementDate] = useState('');
  const [commencement, setCommencement] = useState('');
  const [ex, setEx] = useState('');
  const [expiryUnix, setExpiryUnix] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [coverDays, setCoverDays] = useState('');
  const [numberOfDays] = useState(getDaysInCurrentMonth());
  const [today] = useState(getNow());

  const [reg, setReg] = useState('');
  const [searchTerm2, setSearchTerm2] = useState('');
  const [suggestions2, setSuggestions2] = useState([]);
  const [makeCode, setMakeCode] = useState('');
  const [makeName, setMakeName] = useState('');
  const [searchTerm3, setSearchTerm3] = useState('');
  const [suggestions3, setSuggestions3] = useState([]);
  const [modelCode, setModelCode] = useState('');
  const [modelName, setModelName] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [chassis, setChassis] = useState('');
  const [engineNumber, setEngineNumber] = useState('');
  const [cc, setCC] = useState('');
  const [color, setColor] = useState('');
  const [yom, setYom] = useState('');
  const [file, setFile] = useState(null);
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [vehicleSearchStatus, setVehicleSearchStatus] = useState(null); // null | 'success' | 'failed'
  const [confirmText, setConfirmText] = useState('CONFIRM DETAILS');

  // ── Double insurance ──
  const [doubleStatus, setDoubleStatus] = useState(null); // null=unchecked, true=found, false=clear
  const [doubleMessage, setDoubleMessage] = useState('');
  const [doubleChecking, setDoubleChecking] = useState(false);

  // ── New client modal ──
  const [showClientModal, setShowClientModal] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [otherNames, setOtherNames] = useState('');
  const [ncPhone, setNcPhone] = useState('');
  const [ncEmail, setNcEmail] = useState('');
  const [ncEmailValid, setNcEmailValid] = useState(true);
  const [ncEmailError, setNcEmailError] = useState('');
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [ncPin, setNcPin] = useState('');
  const [ncPinValid, setNcPinValid] = useState(null);
  const [ncPinError, setNcPinError] = useState('');
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [file3, setFile3] = useState(null);
  const [submitText, setSubmitText] = useState('SUBMIT');

  // ── Confirm modal ──
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPressed2, setIsPressed2] = useState(false);
  const [confirmPayText, setConfirmPayText] = useState('CONFIRM DETAILS');

  // ── Payment modal ──
  const [showPayModal, setShowPayModal] = useState(false);
  const [walletPaymentError, setWalletPaymentError] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [proposalOID, setProposalOID] = useState('');
  const [insuredOID, setInsuredOID] = useState('');
  const [insuredSchedule, setInsuredSchedule] = useState('');
  const [coverType, setCoverType] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [payText, setPayText] = useState('SEND REQUEST');
  const [resendText, setResendText] = useState('RESEND REQUEST');
  const [promptId, setPromptId] = useState(0);
  const [isMpesa, setIsMpesa] = useState(true);
  const [prompt, setPrompt] = useState(false);
  const [paymentCountdown, setPaymentCountdown] = useState(PAYMENT_TIMER_SECONDS);
  const [showPaymentTimer, setShowPaymentTimer] = useState(false);
  const paymentTimerRef = useRef(null);
  const [transactionId, setTransactionId] = useState('');
  const [retryText, setRetryText] = useState('RETRY');
  const [docNo, setDocNo] = useState('');
  const [noteOID, setNoteOID] = useState('');

  // ── Certificate ──
  const [isCert, setIsCert] = useState(0); // 0=pending, 1=success, 2=error
  const [certURL, setCertURL] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Mpesa verify modal ──
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [checkMpesa, setCheckMpesa] = useState(false);
  const [mpesaCode, setMpesaCode] = useState('');
  const [verifyText, setVerifyText] = useState('VERIFY');
  const [checkPayment, setCheckPayment] = useState('Checking payment please wait...');

  // ── Wallet / commission toggles ──
  const [paymentMode, setPaymentMode] = useState(0);
  const [utilize, setUtilize] = useState(0);

  // ══════════════════════════════════════
  //  AUTH HEADERS HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(
    () => ({
      Authorization: `Basic ${credentials}`,
      'Content-Type': 'application/json',
    }),
    [credentials]
  );

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        method: 'GET',
        headers: authHeaders(),
        ...options,
      });
      if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
      return res.json();
    },
    [authHeaders]
  );

  // Abort controller ref for cleanup of in-flight requests
  const abortRef = useRef(null);

  // ── Debounce timer ref for client search ──
  const searchDebounceRef = useRef(null);
  const makeSearchDebounceRef = useRef(null);
  const modelSearchDebounceRef = useRef(null);
  const doubleInsDebounceRef = useRef(null);

  // ══════════════════════════════════════
  //  LOGGING HELPER
  // ══════════════════════════════════════
  const insertLog = useCallback(
    async (agentId, activity) => {
      try {
        await fetch(`${API}/insertLogs?activity=${encodeURIComponent(activity)}&agent=${agentId}`, {
          headers: authHeaders(),
        });
      } catch (e) {
        console.error('Log insert failed', e);
      }
    },
    [authHeaders]
  );

  // ══════════════════════════════════════
  //  FILE UPLOAD HELPER
  // ══════════════════════════════════════
  const uploadFile = useCallback(async (fileData, agentId, proposalId, endpoint = 'uploadDocument') => {
    if (!fileData) return;
    const formData = new FormData();
    formData.append('file', fileData);
    formData.append('agent', agentId);
    formData.append('proposal', proposalId);
    try {
      await axios.post(`${API}/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.error(`Upload ${endpoint} failed`, e);
    }
  }, []);

  const uploadClientDocs = useCallback(
    async (proposalId) => {
      await Promise.all([
        uploadFile(file1, agentCode, proposalId, 'uploadIdentity'),
        uploadFile(file2, agentCode, proposalId, 'uploadIRA'),
        uploadFile(file3, agentCode, proposalId, 'uploadKRA'),
      ]);
    },
    [file1, file2, file3, agentCode, uploadFile]
  );

  // ══════════════════════════════════════
  //  CERTIFICATE HELPERS
  // ══════════════════════════════════════
  const buildCertPayload = useCallback(() => ({
    IOID: insuredSchedule, insuredOID, dateDone: getNow(), selectedItem, proposalOID,
    coverType, clientName, policyNumber, commencementUnix, commencementDate,
    expiryUnix, expiryDate, reg, chassis,
    clientPhone: clientPhone ? clientPhone.slice(1) : '',
    bodyType, capacity, makeName, modelName, engineNumber, clientEmail,
    sumInsured, clientPin, yom, iraCode, tonnage, iraNumber,
  }), [insuredSchedule, insuredOID, selectedItem, proposalOID, coverType, clientName, policyNumber, commencementUnix, commencementDate, expiryUnix, expiryDate, reg, chassis, clientPhone, bodyType, capacity, makeName, modelName, engineNumber, clientEmail, sumInsured, clientPin, yom, iraCode, tonnage]);

  // Test webhook expects a fixed dummy payload shape — only the submitted client, reg,
  // chassis and cover dates are swapped in for the real ones.
  const buildCertPayloadTest = useCallback(() => ({
    capacity: 5,
    Client: clientName,
    PhoneNumber: '790876511',
    Email: 'masila.victor43@gmail.com',
    KRAPin: 'A010130198F',
    Registration: reg,
    Chassis: chassis,
    EngineNumber: '94KGK9411',
    Make: 'ISUZU',
    Model: 'FRR',
    CommencementDate: commencementDate,
    ExpiryDate: expiryDate,
    bodytype: 'BUS',
    SumInsured: '0',
    YOM: '2013',
  }), [clientName, reg, chassis, commencementDate, expiryDate]);

  const insertCertificate = useCallback(async (certLink, certNo) => {
    try {
      await fetch(`${API}/insertCertificate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ proposalOID, insuredOID, expiryUnix, commencementUnix, certNo, certLink, today }),
      });
    } catch (e) { console.error('insertCertificate failed', e); }
  }, [authHeaders, proposalOID, insuredOID, expiryUnix, commencementUnix, today]);

  const insertCertificateError = useCallback(async (errorMes) => {
    try {
      await fetch(`${API}/insertCertificateerror`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ proposalOID, insuredOID, expiryUnix, commencementUnix, errorMes, today }),
      });
    } catch (e) { console.error('insertCertificateError failed', e); }
  }, [authHeaders, proposalOID, insuredOID, expiryUnix, commencementUnix, today]);

  const generateCert = useCallback(async (statusSetter) => {
    statusSetter('GENERATING CERTIFICATE...');
    try {
      const res = await axios.post(
        USE_TEST_CERT ? CERT_WEBHOOK_TEST : CERT_WEBHOOK,
        USE_TEST_CERT ? buildCertPayloadTest() : buildCertPayload(),
        USE_TEST_CERT ? { auth: CERT_WEBHOOK_TEST_AUTH } : undefined
      );
      if (res.data.success) {
        await insertCertificate(res.data.certURL, res.data.certNo);
        setCertURL(res.data.certURL);
        insertLog(agentCode, `Certificate for ${reg} generated`);
        setTimeout(() => setIsCert(1), 1500);
      } else {
        await insertCertificateError(res.data.errorMessage);
        insertLog(agentCode, `Certificate for ${reg} error-${res.data.errorMessage}`);
        setIsCert(2);
        setErrorCode(res.data.errorCode);
        setErrorMessage(res.data.errorMessage);
      }
    } catch (e) {
      toast.error('Certificate generation failed');
      console.error(e);
    }
  }, [buildCertPayload, buildCertPayloadTest, insertCertificate, insertCertificateError, insertLog, agentCode, reg]);

  // Before posting a new DMVIC request, check whether a certificate for this proposal
  // was already generated (or already failed) earlier today — avoids firing a duplicate
  // DMVIC request for the same policy on the same day.
  const requestCertificate = useCallback(async (statusSetter) => {
    statusSetter('CHECKING CERTIFICATE...');
    try {
      const check = await apiFetch(`${API}/checkCertToday?proposalOID=${proposalOID}`);
      if (check.found && check.success) {
        setCertURL(check.certURL);
        setTimeout(() => setIsCert(1), 1500);
        return;
      }
      if (check.found && !check.success) {
        setErrorMessage(check.errorMessage);
        setIsCert(2);
        return;
      }
    } catch (e) {
      console.error('checkCertToday failed', e);
      // Fall through to a normal generation attempt — a failed check shouldn't block the agent.
    }
    await generateCert(statusSetter);
  }, [apiFetch, proposalOID, generateCert]);

  // ══════════════════════════════════════
  //  INITIAL DATA LOADS
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (!user) { navigate('/login'); return; }
    setAgent(user);

    const controller = new AbortController();
    abortRef.current = controller;

    const init = async () => {
      try {
        // Makes/models, product cover options and clients are served from a JSON snapshot
        // the backend keeps on disk (refreshed from SQL every 5 minutes) instead of live
        // DB queries. getCoverOptionsCache needs no auth, so it's fired in parallel with
        // getCredentials — cover options (step 1) show up as soon as that resolves rather
        // than waiting on the credentials round-trip first. Clients is sensitive, so it
        // stays behind Basic Auth and is only fetched once credentials are known.
        const [credRes, coverOptsRes] = await Promise.all([
          fetch(`${API}/getCredentials?username=${COMPANY}`, { signal: controller.signal }),
          fetch(`${API}/getCoverOptionsCache`, { signal: controller.signal }),
        ]);
        const coverOptsData = await coverOptsRes.json();
        setMakemodel(coverOptsData.makemodel);
        setPcoveroptions(coverOptsData.pcoveroptions);

        const credData = await credRes.json();
        const creds = btoa(`${credData.Username}:${credData.Password}`);
        setCredentials(creds);
        setIsAuth(true);

        const authOpts = { headers: { Authorization: `Basic ${creds}`, 'Content-Type': 'application/json' }, signal: controller.signal };
        const clientsData = await fetch(`${API}/getClientsCache`, authOpts).then(r => r.json());
        setClients(clientsData.clients);

        const syncedAt = clientsData.syncedAt || coverOptsData.syncedAt || Date.now();
        setDataSyncedAt({ makemodel: syncedAt, pcoveroptions: syncedAt, clients: syncedAt });
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Init failed', e);
      }
    };
    init();

    return () => controller.abort();
  }, [navigate]);

  // ══════════════════════════════════════
  //  MANUAL REFRESH OF CACHED REFERENCE DATA
  //  Re-reads the backend's JSON snapshot (does not trigger a live SQL query — the
  //  snapshot itself is refreshed on the server's own 5-minute cron).
  // ══════════════════════════════════════
  const refreshCachedData = useCallback(async () => {
    if (!credentials) return;
    setRefreshingData(true);
    try {
      const authOpts = { headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/json' } };
      const [coverOptsData, clientsData] = await Promise.all([
        fetch(`${API}/getCoverOptionsCache`).then(r => r.json()),
        fetch(`${API}/getClientsCache`, authOpts).then(r => r.json()),
      ]);
      setMakemodel(coverOptsData.makemodel);
      setPcoveroptions(coverOptsData.pcoveroptions);
      setClients(clientsData.clients);
      const syncedAt = clientsData.syncedAt || coverOptsData.syncedAt || Date.now();
      setDataSyncedAt({ makemodel: syncedAt, pcoveroptions: syncedAt, clients: syncedAt });
      toast.success('Reference data refreshed');
    } catch (e) {
      console.error('Manual refresh failed', e);
      toast.error('Failed to refresh data');
    } finally {
      setRefreshingData(false);
    }
  }, [credentials]);

  //get additional benefits
  useEffect(() => {
    if (!productCode) return;
    const load = async () => {
      try {
        const data = await apiFetch(`https://digitalapi.definiteassurance.com/api/getAdditionalBenefits?product=${productCode}`);
        setAdditionalBenefits(data);
        

      } catch (e) { console.error(e); }
    };
    load();
  }, [productCode]);

  // --------------------------------------
  //  AGENT DETAILS + WALLET + COMMISSION
  // --------------------------------------
  useEffect(() => {
    if (!agent || !isAuth || !credentials) return;
    const controller = new AbortController();
    const headers = { 'Content-Type': 'application/json', Authorization: `Basic ${credentials}` };
    const opts = { headers, signal: controller.signal };

    const loadAll = async () => {
      try {
        // Fire agent details + IRA number in parallel
        const [dealerRes, iraRes] = await Promise.all([
          fetch(`${API}/getDealerdetails?email=${agent}`, opts).then(r => r.json()),
          fetch(`${API}/getIranumber?agent=${agent}`, opts).then(r => r.json()),
        ]);

        const agentId = dealerRes.OID;
        setAgentCode(agentId);
        setDealerAccountCode(dealerRes.DealerAccount ?? null);
        setAgentName(dealerRes.Dealer_name || agent);
        setBranch(dealerRes.Companyy);
        setDealerCode(dealerRes.Dealer_type);
        setDsfApproved(dealerRes.DSFAllowed);
        setParentIntermediary(dealerRes.ParentIntermediary);
        setApproved(dealerRes.Approved ? 1 : 0);
        setHasBinder(!!dealerRes.HasBinder);
        setIraNumber(iraRes.IRANumber);

        // Now fire wallet + commission in parallel
        const [walRes, commRes] = await Promise.all([
          fetch(`${API}/getWallet?agent=${agentId}`, opts).then(r => r.json()),
          fetch(`${API}/getUtilizedCommission?agent=${agentId}`, opts).then(r => r.json()),
        ]);

        const wNo = walRes.WalletNo;
        setWalletNo(wNo);
        setAvailableCommission(commRes.AvailableCommission > 0 ? commRes.AvailableCommission : 0);

        // Wallet balance - depends on WalletNo
        if (wNo) {
          const balRes = await fetch(`${API}/getWalletbalance?agent=${agentId}`, opts).then(r => r.json());
          setWalletBalance(balRes.Balance);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Agent/wallet load failed', e);
      }
    };
    loadAll();

    return () => controller.abort();
  }, [agent, isAuth, credentials]);

  // --------------------------------------
  //  PRODUCT SELECTION & COVER OPTIONS
  // --------------------------------------
  useEffect(() => {
    if (!productCode || !isAuth) return;
    const load = async () => {
      try {
        const data = await apiFetch(`${API}/getThisproduct?OID=${productCode}`);
        setProductName(data.ProductDescription);
        setPrdCode(data.ProductCode.replace(/PRD-/g, ''));
        setCommissionRate(data.CommissionRate);
        setIraCode(data.IRACode);
        setRiskClass(data.RiskClass);
      } catch (e) { console.error(e); }
    };
    load();

    // Filter cover options from prefetched data
    if (pcoveroptions?.data) {
      const found = pcoveroptions.data.find((i) => i.product.OID == productCode);
      setCoverOptions(found ? found.coverOptions : []);
    }
  }, [productCode, isAuth, apiFetch, pcoveroptions]);

  // ── Cover option details ──
  useEffect(() => {
    if (!coverOptionCode || !isAuth) return;
    let cancelled = false;
    // Clear the previous cover option's item/details so a stale insured item is never
    // sent to calculatePremium while the new ones load.
    setInsuredItems([]);
    setSelectedItem('');
    setCoverOption(null);
    const load = async () => {
      try {
        const [itemRes, detRes] = await Promise.all([
          apiFetch(`${API}/getInsureditems?coverOption=${coverOptionCode}`),
          apiFetch(`${API}/getCoveroptiondetails?cover=${coverOptionCode}`),
        ]);
        if (cancelled) return;
        setInsuredItems(itemRes);
        setSelectedItem(itemRes.length === 1 ? itemRes[0].OID : '');
        setCoverOption(detRes.CoverOption);
      } catch (e) { if (!cancelled) console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [coverOptionCode, isAuth, apiFetch]);

  // ── Cover type mapping ──
  useEffect(() => {
    if (coverOption === 1 || coverOption === 58) setCoverType(100);
    else if (coverOption === 2) setCoverType(200);
  }, [coverOption]);

  // ── Fetch additional benefits based on product ──
  useEffect(() => {
    if (!productCode || !isAuth) return;
    let cancelled = false;
    (async () => {
      try {
        const benefits = await apiFetch(`${API}/getAdditionalbenefits?product=${productCode}`);
        if (!cancelled) {
          setAdditionalBenefits(benefits?.data || []);
          setSelectedBenefits([]);
          setBenefitPremiumTotal(0);
        }
      } catch (e) {
        console.error('Failed to fetch additional benefits:', e);
        if (!cancelled) setAdditionalBenefits([]);
      }
    })();
    return () => { cancelled = true; };
  }, [productCode, isAuth]);

  // ── Model name + body — combined into one effect ──
  useEffect(() => {
    if (!modelCode || !isAuth) return;
    let cancelled = false;
    const load = async () => {
      try {
        const modelData = await apiFetch(`${API}/getModelname?model=${modelCode}`);
        if (cancelled) return;
        setModelName(modelData.Description);
        if (modelData.BodyType) {
          const bodyData = await apiFetch(`${API}/getBodyname?body=${modelData.BodyType}`);
          if (!cancelled) setBodyType(bodyData.Description);
        }
      } catch (e) { if (!cancelled) console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [modelCode, isAuth, apiFetch]);

  // ── Client details ──
  // ── Client details — load once when client is selected, cache for confirm modal ──
  useEffect(() => {
    if (!selectedClient || !isAuth) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [idRes, detRes] = await Promise.all([
          apiFetch(`${API}/getClientid?client=${selectedClient}`),
          apiFetch(`${API}/getClientdetails?client=${selectedClient}`),
        ]);
        if (cancelled) return;
        setClient(idRes.IdentificationNo);
        setClientEmail(detRes.Email);
        setClientName(detRes.Dealer_name);
        setClientFullname(detRes.Dealer_name);
        setClientPin(detRes.Pin);
        setClientPhone(detRes.Mobile);
      } catch (e) { if (!cancelled) console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedClient, isAuth, apiFetch]);

  // ── Region / Branch — IntermediaryBranches, only for agent 71693 ──
  const needsBranch = Number(agentCode) === 71693;

  useEffect(() => {
    if (!needsBranch || !isAuth) return;
    (async () => {
      try {
        const data = await apiFetch(`${API}/getIntermediaryBranches`);
        setBranchOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load branches', e);
      }
    })();
  }, [needsBranch, isAuth, apiFetch]);

  const regionOptions = useMemo(
    () => [...new Set(branchOptions.map((b) => b.Region))],
    [branchOptions]
  );

  const branchesForRegion = useMemo(
    () => branchOptions.filter((b) => b.Region === selectedRegion),
    [branchOptions, selectedRegion]
  );

  // ── Check double insurance with new dates ──
  const checkDoubleInsurance = useCallback((commenceDate, endDate) => {
    if (!reg || !chassis || !commenceDate) return;
    
    (async () => {
      try {
        setDoubleChecking(true);
        setDoubleStatus(null);
        const res = await axios.post(DOUBLE_INS_WEBHOOK, {
          chasis: chassis,
          reg,
          commence: commenceDate,
          end: endDate,
        });
        if (res.data.success) {
          setDoubleStatus(true);
          setDoubleMessage(`Vehicle has active certificate in ${res.data.company}. Expected expiry: ${res.data.endDate}`);
        } else {
          setDoubleStatus(false);
          setDoubleMessage('No double insurance');
        }
      } catch (e) {
        console.error('Double insurance check failed:', e);
        setDoubleStatus(false);
        setDoubleMessage('No double insurance');
      } finally {
        setDoubleChecking(false);
      }
    })();
  }, [reg, chassis]);

  // ── Double insurance check — debounced, and re-runs whenever registration, chassis
  // (entered manually or auto-filled by the NTSA search) or the cover period change ──
  useEffect(() => {
    if (!(reg && chassis && commencementDate && expiryDate)) return;

    if (doubleInsDebounceRef.current) clearTimeout(doubleInsDebounceRef.current);
    doubleInsDebounceRef.current = setTimeout(() => {
      checkDoubleInsurance(commencementDate, expiryDate);
    }, DOUBLE_INS_DEBOUNCE_MS);

    return () => clearTimeout(doubleInsDebounceRef.current);
  }, [reg, chassis, commencementDate, expiryDate, checkDoubleInsurance]);

  // ── Payment mode toggles ──
  useEffect(() => { setIsMpesa(paymentMode === 0 && utilize === 0); }, [paymentMode, utilize]);

  // ══════════════════════════════════════
  //  SEARCH HANDLERS — build lowercase lookup maps once
  // ══════════════════════════════════════

  // Pre-build lowercase searchable fields for clients (avoids repeated toLowerCase in filter)
  const clientsIndex = useMemo(
    () => clients.map(c => ({
      ...c,
      _search: `${c.FirstName} ${c.Surname} ${c.IdentificationNo}`.toLowerCase(),
    })),
    [clients]
  );

  const handleClientSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm(v);
    
    // Clear existing debounce timer
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    
    if (v.length > 0) {
      // Show loading state while debouncing
      setSuggestions([]);
      
      // Debounce the actual filtering by 300ms
      searchDebounceRef.current = setTimeout(() => {
        const q = v.toLowerCase();
        setSuggestions(clientsIndex.filter(c => c._search.includes(q)));
      }, 300);
    } else {
      setSuggestions([]);
    }
  }, [clientsIndex]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (makeSearchDebounceRef.current) clearTimeout(makeSearchDebounceRef.current);
      if (modelSearchDebounceRef.current) clearTimeout(modelSearchDebounceRef.current);
      if (doubleInsDebounceRef.current) clearTimeout(doubleInsDebounceRef.current);
    };
  }, []);

    // Allowed makes OIDs for Agricultural product (3592)
  const AGRICULTURAL_MAKES = [670, 3833, 2756, 720, 706, 3801];

  // Pre-build lowercase make descriptions
  const makesIndex = useMemo(() => {
    if (!makemodel?.data) return [];
    const raw = makemodel.data.map(i => ({
      make: i.make,
      models: i.models,
      _search: i.make.Description.toLowerCase(),
    }));
    // If product is Agricultural (3592), only show allowed makes
    if (productCode == 3592) {
      return raw.filter(item => AGRICULTURAL_MAKES.includes(item.make.OID));
    }
    return raw;
  }, [makemodel, productCode]);

  const handleMakeSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm2(v);
    
    if (makeSearchDebounceRef.current) clearTimeout(makeSearchDebounceRef.current);
    
    if (v.length > 0) {
      setSuggestions2([]);
      makeSearchDebounceRef.current = setTimeout(() => {
        const q = v.toLowerCase();
        setSuggestions2(makesIndex.filter(i => i._search.includes(q)).map(i => i.make));
      }, 300);
    } else setSuggestions2([]);
  }, [makesIndex]);

  const handleModelSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm3(v);
    
    if (modelSearchDebounceRef.current) clearTimeout(modelSearchDebounceRef.current);
    
    if (v.length > 0) {
      setSuggestions3([]);
      modelSearchDebounceRef.current = setTimeout(() => {
        const q = v.toLowerCase();
        setSuggestions3(models.filter(m => m.Description.toLowerCase().includes(q)));
      }, 300);
    } else setSuggestions3([]);
  }, [models]);

  const selectClient = (c) => { setSearchTerm(`${c.FirstName} ${c.Surname}`); setSelectedClient(c.OID); setSuggestions([]); };
  const selectMake = (m) => {
    setSearchTerm2(m.Description);
    setMakeCode(m.OID);
    setMakeName(m.Description);
    setSuggestions2([]);
    const found = makesIndex.find((i) => i.make.OID === m.OID);
    setModels(found ? found.models : []);
  };
  const selectModel = (m) => { setSearchTerm3(m.Description); setModelCode(m.OID); setSuggestions3([]); };

  // ══════════════════════════════════════
  //  LOG BOOK UPLOAD
  //  Just attaches the file for the existing uploadFile/uploadDocument flow on submit —
  //  no client-side extraction happens here anymore (see handleVehicleSearch below).
  // ══════════════════════════════════════
  const handleLogbookUpload = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  // ══════════════════════════════════════
  //  NTSA VEHICLE SEARCH
  //  Looks up the entered registration number against NTSA and pre-fills the Vehicle
  //  Details form from the result. Matches Make/Model against the already-fetched
  //  makesmodels reference data, same fuzzy-matching approach the old logbook OCR used.
  //  "N/A"/"NA"/blank fields from NTSA are left alone for the user to fill in.
  //
  //  Double insurance check: setChassis below flows into the SAME `reg`/`chassis` state
  //  the manual inputs use, so the existing "check double insurance when reg or chassis
  //  changes" effect (keyed on [reg, chassis]) fires on its own once chassis is set here -
  //  no separate trigger needed.
  // ══════════════════════════════════════
  const isUsableNTSAValue = (val) => {
    if (val === null || val === undefined) return false;
    const v = String(val).trim();
    return v.length > 0 && v.toUpperCase() !== 'N/A' && v.toUpperCase() !== 'NA';
  };

  const handleVehicleSearch = async () => {
    if (!reg) { toast.error('Enter a registration number first'); return; }

    setVehicleSearchStatus(null);
    setVehicleSearchLoading(true);

    try {
      const { data } = await axios.post('https://portalapi.definiteassurance.com/api/common/SearchVehicleNTSA', {
        vehicleRegistrationNumber: reg,
      });

      if (data.responseCode === 200 && data.vehicle) {
        const v = data.vehicle;
        let foundAny = false;

        if (isUsableNTSAValue(v.chassisNumber)) { setChassis(v.chassisNumber); foundAny = true; }
        if (isUsableNTSAValue(v.engineNumber)) { setEngineNumber(v.engineNumber); foundAny = true; }
        if (isUsableNTSAValue(v.cubicCapacity)) { setCC(v.cubicCapacity); foundAny = true; }
        if (isUsableNTSAValue(v.color)) { setColor(v.color); foundAny = true; }

        if (isUsableNTSAValue(v.vehicleRegistrationYear)) {
          // NTSA sometimes returns a 2-digit year (e.g. "96") — assume 1900s for those.
          const year = /^\d{2}$/.test(v.vehicleRegistrationYear) ? `19${v.vehicleRegistrationYear}` : v.vehicleRegistrationYear;
          setYom(year);
          foundAny = true;
        }

        if (isUsableNTSAValue(v.vehicleMake)) {
          const q = v.vehicleMake.toLowerCase();
          const matchedMake = makesIndex.find((i) => i._search.includes(q) || q.includes(i._search));
          if (matchedMake) {
            selectMake(matchedMake.make);
            foundAny = true;

            if (isUsableNTSAValue(v.vehicleModel)) {
              const qm = v.vehicleModel.toLowerCase();
              const matchedModel = matchedMake.models.find(
                (m) => m.Description.toLowerCase().includes(qm) || qm.includes(m.Description.toLowerCase())
              );
              if (matchedModel) selectModel(matchedModel);
            }
          }
        }

        if (foundAny) {
          setVehicleSearchStatus('success');
          toast.success('Vehicle details found — please review the fields below.');
        } else {
          setVehicleSearchStatus('failed');
          toast.warn('No usable vehicle details found. Please fill in the details manually.');
        }
      } else {
        setVehicleSearchStatus('failed');
        toast.warn(data.errorMessage || 'Vehicle not found. Please fill in the details manually.');
      }
    } catch (err) {
      console.error('NTSA vehicle search failed', err);
      setVehicleSearchStatus('failed');
      toast.error('Vehicle search failed. Please fill in the details manually.');
    } finally {
      setVehicleSearchLoading(false);
    }
  };

  // ══════════════════════════════════════
  //  DATE HANDLING
  // ══════════════════════════════════════
  const handleDateChange = (e) => {
    const ms = new Date(e.target.value).getTime() + 60000;
    if (ms < Date.now()) { toast.error('Commencement date cannot be in the past'); return; }

    const newCommencementDate = formatDateFromMs(ms);
    
    setCommencement(ms);
    setCommencementDate(newCommencementDate);
    setCommencementUnix(formatDateTime(ms));
    setCm(formatDateTime(ms));

    // Calculate expiry
    const monthMs = { 30: 2592000000 - 86400000, 31: 2592000000, 28: 2419200000 - 86400000, 29: 2419200000 };
    const durations = {
      Monthly: monthMs[numberOfDays], '3installments': monthMs[numberOfDays], '6Installments': monthMs[numberOfDays],
      '10Installments': monthMs[numberOfDays], '2installments': monthMs[numberOfDays],
      Week: 604800000 - 86400000, Fortnight: 1209600000 - 86400000, Annually: 31536000000 - 86400000,
    };

    const exp = ms + (durations[coverPeriod] || 0);
    const expiryD = new Date(exp);
    expiryD.setHours(23, 59, 0, 0);
    const expMs = expiryD.getTime();

    const newExpiryDate = formatDateFromMs(expMs);
    
    setExpiryDate(newExpiryDate);
    setExpiryUnix(formatDateTime(expMs));
    setEx(formatDateTime(expMs));

    const dayMap = { Monthly: 30, '3installments': 30, '6Installments': 30, '10Installments': 30, '2installments': 30, Week: 7, Fortnight: 7, Annually: 365 };
    setCoverDays(dayMap[coverPeriod] || 30);
    // Double insurance check re-runs on its own via the debounced [reg, chassis,
    // commencementDate, expiryDate] effect once these state updates land.
  };

  // ── Handle additional benefit selection ──
  const handleBenefitChange = useCallback((benefit) => {
    setSelectedBenefits((prev) => {
      const isSelected = prev.some((b) => b.OID === benefit.OID);
      if (isSelected) {
        return prev.filter((b) => b.OID !== benefit.OID);
      } else {
        return [...prev, benefit];
      }
    });
  }, []);

  // ── Recalculate premium when benefits change ──
  useEffect(() => {
    if (!basePremium || selectedBenefits.length === 0) {
      setBenefitPremiumTotal(0);
      // Reset to base premium if no benefits selected
      if (basePremium) {
        const tl = Math.round(0.002 * basePremium);
        const ph = Math.round(0.0025 * basePremium);
        const gross = basePremium + tl + ph + STAMP_DUTY;
        const com = Math.round((parseInt(commissionRate) / 100) * basePremium);
        const netCom = com - Math.round(0.1 * com);
        
        setPremium(basePremium);
        setTrainingLevy(tl);
        setPHCF(ph);
        setBasicPremium(gross);
        setPayAmount(gross);
        setCommission(com);
        setNetCommission(netCom);
        setNetPremium(gross - netCom);
      }
      return;
    }

    let totalBenefit = 0;
    selectedBenefits.forEach((benefit) => {
      // Calculate: MinimumRate * sumInsured / 100
      const calculated = (benefit.MinimumRate * (sumInsured || 0)) / 100;
      // Use MinimumPremium if calculated is less
      const benefitAmount = calculated < benefit.MinimumPremium ? benefit.MinimumPremium : calculated;
      totalBenefit += benefitAmount;
    });

    setBenefitPremiumTotal(totalBenefit);
    
    // Recompute with base premium + benefits
    if (totalBenefit > 0) {
      const newPremium = basePremium + totalBenefit;
      const tl = Math.round(0.002 * newPremium);
      const ph = Math.round(0.0025 * newPremium);
      const gross = newPremium + tl + ph + STAMP_DUTY;
      const com = Math.round((parseInt(commissionRate) / 100) * newPremium);
      const netCom = com - Math.round(0.1 * com);
      
      setPremium(newPremium);
      setTrainingLevy(tl);
      setPHCF(ph);
      setBasicPremium(gross);
      setPayAmount(gross);
      setCommission(com);
      setNetCommission(netCom);
      setNetPremium(gross - netCom);
    }
  }, [selectedBenefits, sumInsured, basePremium, commissionRate]);

  // ══════════════════════════════════════
  //  PREMIUM CALCULATION HELPER
  // ══════════════════════════════════════
  const computePremiumResults = (premiumVal) => {
    const tl = Math.round(0.002 * premiumVal);
    const ph = Math.round(0.0025 * premiumVal);
    const gross = premiumVal + tl + ph + STAMP_DUTY;
    const com = Math.round((parseInt(commissionRate) / 100) * premiumVal);
    const netCom = com - Math.round(0.1 * com);

    // A fresh quote always replaces the base premium and clears any benefits picked
    // against a previous quote.
    setPremium(premiumVal);
    setBasePremium(premiumVal);
    setSelectedBenefits([]);
    setBenefitPremiumTotal(0);
    setTrainingLevy(tl);
    setPHCF(ph);
    setBasicPremium(gross);
    setPayAmount(gross);
    setCommission(com);
    setNetCommission(netCom);
    setNetPremium(gross - netCom);
  };

  // ══════════════════════════════════════
  //  SELECT PRODUCT — start a fresh quote, discarding the previous product's
  //  cover option, payment interval and calculated premium
  // ══════════════════════════════════════
  const selectProduct = (code) => {
    setCoverOptionCode('');
    setCoverOption(null);
    setInsuredItems([]);
    setSelectedItem('');
    setCoverPeriod('');
    setSumInsured(0);
    setCapacity(0);
    setTonnage(0);
    setPremium(null);
    setPremiumRate(null);
    setBasePremium(null);
    setPHCF(null);
    setTrainingLevy(null);
    setBasicPremium(0);
    setCommission('');
    setNetCommission('');
    setNetPremium('');
    setPayAmount(0);
    setSelectedBenefits([]);
    setBenefitPremiumTotal(0);
    setProductCode(code);
    setShowQuoteModal(true);
  };

  // ══════════════════════════════════════
  //  GET QUOTATION
  // ══════════════════════════════════════
  const getQuote = async () => {
    if (dealerCode === 170 && dsfApproved !== '1') { toast.error('You need an agent account to proceed'); return; }
    if (!approved) { toast.error('Your account is not approved. Kindly contact support!'); insertLog(agentCode, 'Premium calculation failed! Account not approved'); return; }
    if (!productCode || !coverOptionCode) { toast.error('Insufficient information'); return; }

    const isComp = coverOption === 1 || coverOption === 58;
    const isTP = coverOption === 2;

    if (isComp) {
      const minSI = LOW_SUM_PRODUCTS.includes(Number(productCode)) ? 100000 : 500000;
      if (sumInsured < minSI) { toast.error(`Sum Insured cannot be below Kes. ${formatter(minSI)}`); return; }
    }
    if (isTP && !capacity) { toast.error('Specify capacity'); return; }
    if ([3582, 3583].includes(Number(productCode)) && Number(capacity) > 7) { toast.error('Capacity cannot exceed 7 for Private Hire products'); return; }
    if (isTP && Number(productCode) === 4695 && tonnage > 3) { toast.error('Tonnage should be below 3 Tons'); return; }

    try {
      setQuoteText('CALCULATING...');
      const params = new URLSearchParams({
        insuredItem: selectedItem, coverOption: coverOptionCode, sumInsured,
        capacity, coverPeriod, product: productCode, tonnage,
        HasBinder: hasBinder ? 1 : 0,
        agentCode: hasBinder && dealerAccountCode ? dealerAccountCode : agentCode,
      });
      const data = await apiFetch(`${API}/calculatePremium?${params}`);

      if (data.premium) {
        if (data.insuredItem) setSelectedItem(data.insuredItem);
        setPremiumRate(data.PremiumRate ?? null);
        computePremiumResults(data.premium);
        insertLog(agentCode, `Premium calculated for product ${productName}. Amount: Kes ${data.premium + Math.round(0.002 * data.premium) + Math.round(0.0025 * data.premium) + STAMP_DUTY}`);
        setShowQuoteModal(false);
        setStep(2);
      } else {
        toast.error('NO PREMIUM SET! CONTACT SUPPORT');
        insertLog(agentCode, `Premium for product ${productName} not found`);
      }
    } catch (e) {
      toast.error('Calculation failed');
      console.error(e);
    } finally {
      setQuoteText('GET QUOTATION');
    }
  };

  // ══════════════════════════════════════
  //  OPEN CONFIRM MODAL — uses already-cached client details, no re-fetch
  // ══════════════════════════════════════
  const openConfirmModal = () => {
    if (!selectedClient) { toast.error('Please select a client first'); return; }
    if (!clientFullname) { toast.error('Client details are still loading...'); return; }
    setShowConfirmModal(true);
  };

  // ══════════════════════════════════════
  //  DOWNLOAD QUOTE AS PDF (Professional Design)
  // ══════════════════════════════════════
  const downloadQuotePDF = async () => {
    try {
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const primaryColor = [26, 92, 90];     // #1a5c5a
      const accentColor = [231, 76, 60];    // #e74c3c
      const secondaryColor = [39, 174, 96]; // #27ae60
      const lightGray = [245, 245, 245];
      const darkGray = [85, 85, 85];
      const mediumGray = [127, 140, 141];
      
      let yPosition = 10;

      // ─── HEADER SECTION ───
      doc.setDrawColor(...primaryColor);
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 35, 'F');

      // Logo with white background container
      try {
        const img = await new Promise((resolve) => {
          const image = new Image();
          image.src = '/definitelogo.png';
          image.onload = () => resolve(image);
          image.onerror = () => resolve(null);
        });
        if (img) {
          // White background container with rounded corners
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(10, 3, 30, 16, 2, 2, 'F');
          doc.addImage(img, 'PNG', 12, 5, 25, 12);
        }
      } catch (e) {
        console.error('Logo loading failed', e);
      }

      // Company name in header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('DEFINITE ASSURANCE', 50, 14);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 200, 200);
      doc.text('You Are Definitely Covered', 50, 19);

      // "QUOTATION" title on right
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.text('QUOTATION', pageWidth - 15, 16, { align: 'right' });

      yPosition = 42;

      // ─── QUOTATION INFO & COMPANY DETAILS (Two Columns) ───
      // Left column: Quote details
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...darkGray);
      doc.text('Quote Information', 15, yPosition);

      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);

      const quoteNumber = uuidv4().substring(0, 8).toUpperCase();
      const quoteDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const validUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      doc.text(`Quote #: DA-${quoteNumber}`, 15, yPosition);
      yPosition += 4;
      doc.text(`Date: ${quoteDate}`, 15, yPosition);
      yPosition += 4;
     

      // Right column: Company contact
      yPosition = 47;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...darkGray);
      doc.text('Contact Us', pageWidth - 60, yPosition);

      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text('+254 708 247 247', pageWidth - 60, yPosition);
      yPosition += 3;
      doc.text('+254 788 247 247', pageWidth - 60, yPosition);
      yPosition += 3;
      doc.text('customerservice@definiteassurance.com', pageWidth - 60, yPosition);
      yPosition += 3;
      doc.text('ABSA Towers, Loita Street, Nairobi', pageWidth - 60, yPosition);

      yPosition = 65;

      // ─── HORIZONTAL DIVIDER ───
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(15, yPosition, pageWidth - 15, yPosition);

      yPosition += 8;

      // ─── POLICY DETAILS SECTION ───
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...primaryColor);
      doc.text('Policy Details', 15, yPosition);

      yPosition += 6;
      
      doc.setFillColor(...lightGray);
      doc.rect(15, yPosition - 3, pageWidth - 30, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text('Product', 18, yPosition);

      doc.text('Cover Days', 135, yPosition);
      doc.text('Commencement', 170, yPosition);
      doc.text('Expiry', 220, yPosition);

      yPosition += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      
      const dayMapPdf = { Monthly: 30, '3installments': 30, '6Installments': 30, '10Installments': 30, '2installments': 30, Week: 7, Fortnight: 14, Annually: 365 };
      const coverDaysPdf = dayMapPdf[coverPeriod] || 30;
      
      doc.text(productName || 'N/A', 18, yPosition);
   
      doc.text(String(coverDaysPdf), 135, yPosition);
      doc.text(commencementUnix ? commencementUnix.split(' ')[0] : 'N/A', 170, yPosition);
      doc.text(expiryUnix ? expiryUnix.split(' ')[0] : 'N/A', 220, yPosition);

      yPosition += 7;

      // ─── ADDITIONAL DETAILS ───
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Vehicle Registration: ${reg || 'N/A'}`, 18, yPosition);
      yPosition += 4;
      doc.text(`Sum Insured: KES ${formatter(sumInsured || 0)}`, 18, yPosition);

      yPosition += 8;

      // ─── PREMIUM BREAKDOWN TABLE ───
      // Header
      doc.setFillColor(...primaryColor);
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition - 2, pageWidth - 30, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('Premium Breakdown', 18, yPosition + 2);
      doc.text('Amount (KES)', pageWidth - 35, yPosition + 2, { align: 'right' });

      yPosition += 8;

      // Table rows
      const breakdownRows = [
        { label: 'Base Premium', value: premium || 0, normal: true },
        { label: 'Net Premium', value: netPremium || 0, normal: true },
        { label: 'PHCF', value: PHCF || 0, normal: true },
        { label: 'Training Levy', value: trainingLevy || 0, normal: true },
        { label: 'Stamp Duty', value: STAMP_DUTY, normal: true },
      ];

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);

      breakdownRows.forEach((row, idx) => {
        if (idx % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(15, yPosition - 2.5, pageWidth - 30, 5, 'F');
        }
        doc.text(row.label, 18, yPosition);
        doc.text(formatter(row.value), pageWidth - 18, yPosition, { align: 'right' });
        yPosition += 5;
      });

      // ─── TOTAL (GROSS PREMIUM) ───
      doc.setFillColor(...primaryColor);
      doc.setDrawColor(...accentColor);
      doc.setLineWidth(2);
      doc.rect(15, yPosition - 2.5, pageWidth - 30, 7, 'F');
      doc.line(15, yPosition - 3, pageWidth - 15, yPosition - 3); // Top border
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('TOTAL GROSS PREMIUM', 18, yPosition + 1.5);
      doc.text(`KES ${formatter(basicPremium || 0)}`, pageWidth - 18, yPosition + 1.5, { align: 'right' });

      yPosition += 10;

      // ─── ADDITIONAL BENEFITS (if any) ───
      if (selectedBenefits.length > 0) {
        yPosition += 4;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...primaryColor);
        doc.text('Optional Additional Benefits', 15, yPosition);
        
        yPosition += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        
        selectedBenefits.forEach((benefit) => {
          const calculated = (benefit.MinimumRate * (sumInsured || 0)) / 100;
          const benefitAmount = calculated < benefit.MinimumPremium ? benefit.MinimumPremium : calculated;
          
          doc.text('• ' + benefit.ItemName, 18, yPosition);
          doc.text(`KES ${formatter(benefitAmount)}`, pageWidth - 18, yPosition, { align: 'right' });
          yPosition += 4;
        });

        yPosition += 2;
        doc.setFillColor(...lightGray);
        doc.rect(15, yPosition - 2, pageWidth - 30, 5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(...primaryColor);
        doc.text('Benefits Subtotal', 18, yPosition + 1);
        doc.text(`KES ${formatter(benefitPremiumTotal || 0)}`, pageWidth - 18, yPosition + 1, { align: 'right' });
        
        yPosition += 6;
      }

      yPosition += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      
      doc.setFillColor(...lightGray);
      doc.rect(15, yPosition - 1.5, pageWidth - 30, 4.5, 'F');
      


      yPosition += 6;
      doc.setTextColor(...mediumGray);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);


      yPosition = pageHeight - 20;

      // ─── FOOTER DIVIDER ───
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(15, yPosition, pageWidth - 15, yPosition);

      yPosition += 3;

      // ─── FOOTER ───
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...primaryColor);
      doc.text('DEFINITE ASSURANCE LIMITED', pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 3;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mediumGray);
      doc.text('ABSA Towers 1st Floor, Loita Street, Nairobi | +254 708 247 247', pageWidth / 2, yPosition, { align: 'center' });

      yPosition += 2;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6);
      doc.text(`Generated on ${new Date().toLocaleString()} | Quote #${quoteNumber}`, pageWidth / 2, pageHeight - 3, { align: 'center' });

      // Download
      doc.save(`Quotation_${productName}_${quoteNumber}.pdf`);
      toast.success('Quote downloaded successfully');
    } catch (e) {
      console.error('PDF generation failed', e);
      toast.error('Failed to generate PDF');
    }
  };

  // ══════════════════════════════════════
  //  CREATE PROPOSAL
  // ══════════════════════════════════════
  const proposalBody = useCallback(() => ({
    commissionRate, agentCode, agentName, prdCode, client, commencementUnix, expiryUnix,
    today, coverDays, reg, productCode, payAmount, premium, basicPremium, commission,
    stampduty: STAMP_DUTY, PHCF, trainingLevy, netCommission, netPremium, selectedClient,
    makeCode, modelCode, capacity, color, tonnage, yom, coverOptioncode: coverOptionCode,
    sumInsured, chassis, engineNumber, cc, selectedItem, branch, intermediaryBranch,
  }), [commissionRate, agentCode, agentName, prdCode, client, commencementUnix, expiryUnix, today, coverDays, reg, productCode, payAmount, premium, basicPremium, commission, PHCF, trainingLevy, netCommission, netPremium, selectedClient, makeCode, modelCode, capacity, color, tonnage, yom, coverOptionCode, sumInsured, chassis, engineNumber, cc, selectedItem, branch, intermediaryBranch]);

  const createNewProposal = async () => {
    setIsPressed2(true);
    setConfirmPayText('LOADING...');
    try {
      const checkRes = await apiFetch(`${API}/checkProposalexists?agent=${agentCode}&reg=${reg}`);

      let result;
      if (checkRes.exists1) {
        result = await (await fetch(`${API}/updateExisting`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ ...proposalBody(), selectedClient }),
          credentials: 'include',
        })).json();
      } else if (checkRes.exists2) {
        toast.error('A proposal with the same registration number already exists');
        return;
      } else {
        result = await (await fetch(`${API}/createProposal2`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify(proposalBody()),
          credentials: 'include',
        })).json();
      }

      if (result.success) {
        setInsuredSchedule(result.IOID);
        setPolicyNumber(result.policyNumber);
        setProposalOID(result.proposalOID);
        setInsuredOID(result.insuredOID);
        setDocNo(result.docNo);
        setNoteOID(result.noteOID);
        await uploadFile(file, agentCode, result.proposalOID);
        if (file1 && file3) await uploadClientDocs(result.proposalOID);
        
        // Insert selected additional benefits
        if (selectedBenefits.length > 0) {
          const benefitsPayload = selectedBenefits.map((benefit) => {
            const calculated = (benefit.MinimumRate * (sumInsured || 0)) / 100;
            const itemAmount = calculated < benefit.MinimumPremium ? benefit.MinimumPremium : calculated;
            return {
              ProductItem: benefit.OID,
              InsuredItem: result.insuredOID,
              Proposal: result.proposalOID,
              ItemValue: benefit.MinimumRate,
              ItemAmount: itemAmount,
              ValueType: 1,
              OptimisticLockField: 4,
            };
          });
          
          try {
            await fetch(`${API}/insertAdditionalBenefits`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ benefits: benefitsPayload }),
              credentials: 'include',
            });
          } catch (e) {
            console.error('Failed to insert additional benefits', e);
          }
        }
        
        insertLog(agentCode, `${checkRes.exists1 ? 'Updated' : 'New'} proposal. OID ${result.proposalOID}`);
        setShowConfirmModal(false);
        setShowPayModal(true);
      } else {
        toast.error('Proposal creation failed. Please try again.');
        setConfirmPayText('RETRY');
      }
    } catch (e) {
      toast.error('Network error! Please try again.');
      setConfirmPayText('RETRY');
      console.error(e);
    } finally {
      setIsPressed2(false);
    }
  };

  // ══════════════════════════════════════
  //  MAKE PAYMENT HANDLER
  // ══════════════════════════════════════
  const handleMakePayment = () => {
    if (confirmPayText === 'RETRY') {
      initiatePayment();
      return;
    }
    if (proposalOID) { setShowPayModal(true); return; }
    initiatePayment();
  };

  const initiatePayment = () => {
    if (!selectedClient || !commencement || !reg || !makeCode || !modelCode || !chassis) {
      toast.error('All fields are required!');
      return;
    }
    if (needsBranch && !intermediaryBranch) { toast.error('Select Region and Branch'); return; }
    if (doubleStatus) { toast.error(doubleMessage); return; }
    if ((coverOption === 1 || coverOption === 58) && yom < 2011) {
      toast.error('Year of manufacture cannot be below 2010');
      return;
    }
    createNewProposal();
  };

  // ── Payment countdown (anti-clockwise clock shown while waiting on the STK push) ──
  const startPaymentCountdown = useCallback(() => {
    if (paymentTimerRef.current) clearInterval(paymentTimerRef.current);
    setPaymentCountdown(PAYMENT_TIMER_SECONDS);
    setShowPaymentTimer(true);
    paymentTimerRef.current = setInterval(() => {
      setPaymentCountdown((s) => {
        if (s <= 1) {
          clearInterval(paymentTimerRef.current);
          paymentTimerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  const stopPaymentCountdown = useCallback(() => {
    if (paymentTimerRef.current) {
      clearInterval(paymentTimerRef.current);
      paymentTimerRef.current = null;
    }
    setShowPaymentTimer(false);
  }, []);

  // Clear the countdown interval if the component unmounts mid-wait
  useEffect(() => () => { if (paymentTimerRef.current) clearInterval(paymentTimerRef.current); }, []);

  // ── Mpesa / Wallet / Commission payment ──
  const processPayment = async (statusSetter) => {
    const fmtPhone = '254' + mpesaPhone.slice(1);
    const ref = generateUniqueId();
    const uuid = uuidv4();
    const dateDone = getNow();

    if (paymentMode === 0 && utilize === 0) {
      // MPESA
      statusSetter('CHECKING PAYMENT...');
      toast.success('Prompt sent to phone!');
      setPrompt(true);
      startPaymentCountdown();

      let res;
      try {
        res = await axios.post(PAYMENT_WEBHOOK, { formattedNumber2: fmtPhone, payAmount, reg:docNo, uuid, qref: `QP${ref}`, dateDone }, { timeout: PAYMENT_TIMER_SECONDS * 1000 });
      } catch (e) {
        stopPaymentCountdown();
        toast.error('Payment request failed');
        setPromptId(1);
        statusSetter('SEND REQUEST');
        return;
      }
      stopPaymentCountdown();

        if (res.data.success) {
          setTransactionId(res.data.transactionId);
          insertLog(agentCode, `Payment Kes.${payAmount} for ${reg} received - ${res.data.transactionId}`);
          // Approve & generate cert
          await fetch(`${API}/createProposal`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: res.data.transactionId, noteOID,docNo }),
            credentials: 'include',
          });
          await uploadFile(file, agentCode, proposalOID);
          insertLog(agentCode, `Proposal ${proposalOID} approved`);
          await requestCertificate(statusSetter);

          
        } else {
          setPromptId(1);
          toast.error('PAYMENT NOT RECEIVED');
          statusSetter('SEND REQUEST');
        }

    } else if (paymentMode === 1) {
      // WALLET
      if (payAmount > walletBalance) { toast.error('INSUFFICIENT WALLET BALANCE. TOP UP TO PROCEED'); return; }
      setWalletPaymentError('');
      statusSetter('CHECKING WALLET...');

      try {
        const paywithwallet = await fetch(`${API}/createProposalWithWallet`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID }),
          credentials: 'include',
        });
        const payRes = await paywithwallet.json();
        if (!payRes.success) {
          const message = payRes.message || 'Wallet payment failed. Please try again.';
          toast.error(message);
          setWalletPaymentError(message);
          insertLog(agentCode, `Payment for proposal ${proposalOID} done by wallet failed - ${message}`);
          statusSetter('SEND REQUEST');
          return;
        }
        else {
           await uploadFile(file, agentCode, proposalOID);
          insertLog(agentCode, `Payment for proposal ${proposalOID} done by wallet and Approved`);
          await requestCertificate(statusSetter);

        }


      } catch (e) {
        toast.error('SOMETHING WENT WRONG');
        setWalletPaymentError('Something went wrong. Please try again.');
        statusSetter('SEND REQUEST');
      }
    } else if (utilize === 1) {
      // COMMISSION UTILIZATION
      if (payAmount > availableCommission) { toast.error(`INSUFFICIENT COMMISSION`); return; }
      statusSetter('CHECKING BALANCE...');
      try {
        await fetch(`${API}/createProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 1, ...proposalBody(), proposalOID }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        insertLog(agentCode, `Payment by commission utilization for ${proposalOID}`);
        await requestCertificate(statusSetter);
      } catch (e) { toast.error('SOMETHING WENT WRONG'); statusSetter('SEND REQUEST'); }
    }
  };

  // ── Mpesa manual verify ──
  const checkAutoApprovalAndPay = async () => {
    try {
      const data = await apiFetch(`${API}/checkAutoApproval?proposal=${proposalOID}&payAmount=${payAmount}`);
      if (data.autoApproved && data.paid) {
        setShowVerifyModal(false);
        setIsMpesa(false);
        toast.success('Payment received');
        insertLog(agentCode, `Proposal ${proposalOID} approved`);
        await requestCertificate(setPayText);
      } else if (!data.autoApproved && data.paid) {
        setShowVerifyModal(false);
        toast.success('Payment received');
        // Manual approval + cert
        await fetch(`${API}/approveProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: transactionId }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        await requestCertificate(setPayText);
      } else {
        setShowVerifyModal(true);
        setCheckMpesa(true);
      }
    } catch (e) { toast.error('Check failed'); console.error(e); }
  };

  const verifyMpesaCode = async () => {
    if (!mpesaCode) return;
    try {
      setVerifyText('Verifying...');
      const data = await apiFetch(`${API}/checkMpesa?code=${mpesaCode}&amount=${payAmount}&reg=${reg}`);
      if (data.success) {
        toast.success('Payment received');
        setShowVerifyModal(false);
        // Approve & generate
        await fetch(`${API}/approveProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: mpesaCode }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        await requestCertificate(setPayText);
      } else {
        toast.error('No transaction with that code');
      }
    } catch (e) { toast.error('Verification failed'); }
    finally { setVerifyText('VERIFY'); }
  };

  const retryCertificate = async () => {
    setRetryText('Please wait...');
    await generateCert(setRetryText);
    setRetryText('RETRY');
  };

  // ══════════════════════════════════════
  //  CREATE CLIENT
  // ══════════════════════════════════════
  const createClient = async () => {
    if (!ncPinValid) { toast.error('KRA PIN is invalid'); return; }
    if (!ncPhone || ncPhone.length < 10) { toast.error('Phone must be at least 10 digits'); return; }
    if (!ncEmailValid) { toast.error('Email is invalid'); return; }
    if (`${firstName}${lastName}${otherNames}`.length > 50) { toast.error('Combined names must not exceed 50 chars'); return; }
    if (!firstName || !lastName || !ncPhone || !ncEmail || !idType || !idNumber || !ncPin || !file1 || !file3) {
      toast.error('All fields marked * are required');
      return;
    }
    if (idType === ID_TYPES.NATIONAL_ID && !/^[0-9]+$/.test(idNumber)) {
      toast.error('National ID number must contain digits only');
      return;
    }

    try {
      setSubmitText('PLEASE WAIT...');
      const res = await fetch(`${API}/createClient`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ firstName, lastName, otherNames, phone: ncPhone, email: ncEmail, idType, idNumber, pin: ncPin, agent: agentCode }),
        credentials: 'include',
      });
      const { success, exists, OID } = await res.json();

      if (success && !exists) {
        setSearchTerm(`${firstName} ${lastName}`);
        setSelectedClient(OID);
                // Refresh clients list immediately
        try {
          const freshClients = await fetch(`${API}/getClients`, { headers: authHeaders() }).then(r => r.json());
          setClients(freshClients);
        } catch (e) { console.error('Refresh clients failed', e); }
        insertLog(agentCode, `Client ${firstName} ${lastName} created`);
        toast.success('Client added successfully');
        setShowClientModal(false);
        resetClientForm();
      } else if (!success && exists) {
        toast.error('Details already exist!');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (e) { toast.error('Network error'); console.error(e); }
    finally { setSubmitText('SUBMIT'); }
  };

  const resetClientForm = () => {
    setFirstName(''); setLastName(''); setOtherNames('');
    setNcPhone(''); setNcEmail(''); setIdType(''); setIdNumber(''); setNcPin('');
    setNcPinValid(null); setNcPinError(''); setNcEmailValid(true); setNcEmailError('');
  };

  const finish = () => window.location.reload(false);

  // ── Validation helpers ──
  const fieldClass = (valid) => (valid ? 'valid' : valid === false ? 'invalid' : '');

  // ── Identification type (National ID / Passport / Certificate of Incorporation) ──
  const ID_TYPES = { NATIONAL_ID: '28', PASSPORT: '29', CERT_OF_INCORPORATION: '30' };
  const idNumberLabel = {
    [ID_TYPES.NATIONAL_ID]: 'National ID Number',
    [ID_TYPES.PASSPORT]: 'Passport Number',
    [ID_TYPES.CERT_OF_INCORPORATION]: 'Certificate of Incorporation Number',
  }[idType] || 'ID Number';
  const handleIdNumberChange = (e) => {
    const v = e.target.value;
    setIdNumber(idType === ID_TYPES.NATIONAL_ID ? v.replace(/[^0-9]/g, '') : v);
  };

  // After an NTSA vehicle search, flags whichever Vehicle Details fields it didn't
  // manage to fill so they get an extra highlight until the user fills them in manually.
  const vehicleFieldClass = (val) => {
    const cls = fieldClass(!!val);
    return vehicleSearchStatus !== null && !val ? `${cls} attention` : cls;
  };

  const missingVehicleFields = useMemo(() => {
    if (vehicleSearchStatus === null) return [];
    const missing = [];
    if (!makeCode) missing.push('Vehicle Make');
    if (!modelCode) missing.push('Vehicle Model');
    if (!chassis) missing.push('Chassis Number');
    if (!engineNumber) missing.push('Engine Number');
    if (!cc) missing.push('Engine Rating (CC)');
    if (!color) missing.push('Color');
    if (!yom) missing.push('Year of Manufacture');
    return missing;
  }, [vehicleSearchStatus, makeCode, modelCode, chassis, engineNumber, cc, color, yom]);

  // ══════════════════════════════════════
  //  PAYMENT INTERVAL OPTIONS — memoized
  // ══════════════════════════════════════
  const paymentIntervals = useMemo(() => {
    const pc = Number(productCode);
    const isMatatu = (pc === 4638 && coverOption === 2) || (pc === 3587 && coverOption === 2);
    const isTOR = pc === 4695 || pc === 4694;
    const isBoda = pc === 4652;

    if (isMatatu) {
      return [
        { value: 'Week', label: '7 Days' }, { value: 'Fortnight', label: '14 Days' },
        { value: 'Monthly', label: '30 Days' }, { value: 'Annually', label: 'Annual' },
        { value: '3Installments', label: '3 Installments' }, { value: '6Installments', label: '6 Installments' },
        { value: '10Installments', label: '10 Installments' },
      ];
    }
    if (isTOR) return [{ value: 'Monthly', label: '30 Days' }];
    if (isBoda && coverOptionCode === '3535') return [{ value: 'Monthly', label: '30 Days' }];
    if (isBoda && coverOptionCode === '3422') return [{ value: 'Annually', label: 'Annual' }, { value: '2installments', label: '2 Installments' }];
    if (coverOption === 1 || coverOption === 58) return [{ value: 'Annually', label: 'Annual Quotation' },{ value: 'Monthly', label: '1 Month' }];
    if (coverOption === 2) return [{ value: 'Annually', label: 'Annual' }, { value: '2installments', label: '2 Installments' }];
    return [];
  }, [productCode, coverOption, coverOptionCode]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="getcover-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="getcover-main">
        <div className="gc-top-row">
          <span className="gc-company">DEFINITE ASSURANCE</span>
          <span className="gc-breadcrumb">Get Cover / New Cover</span>
          <span className="gc-data-sync" style={{ marginLeft: 'auto', fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
            {(() => {
              const timestamps = [dataSyncedAt.makemodel, dataSyncedAt.pcoveroptions, dataSyncedAt.clients].filter(Boolean);
              const oldest = timestamps.length ? Math.min(...timestamps) : null;
              return oldest ? `Reference data synced: ${formatDateTime(oldest)}` : 'Reference data not yet synced';
            })()}
            <button
              type="button"
              onClick={refreshCachedData}
              disabled={refreshingData || !credentials}
              title="Refresh cover options, clients, makes & models now"
              style={{ border: 'none', background: 'none', cursor: refreshingData ? 'default' : 'pointer', color: '#1a5c5a', fontSize: 11, textDecoration: 'underline', padding: 0 }}
            >
              {refreshingData ? 'Refreshing…' : 'Refresh'}
            </button>
          </span>
        </div>

        {/* ── Step indicator ── */}
        <div className="gc-steps">
          {['Select Product', 'Quotation', 'Risk Details'].map((label, i) => (
            <div key={i} className={`gc-step ${step === i + 1 ? 'active' : step > i + 1 ? 'completed' : ''}`}>
              <span className="gc-step-number">{step > i + 1 ? <FaCheck size={10} /> : i + 1}</span>
              {label}
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════
            STEP 1 — SELECT PRODUCT
           ══════════════════════════════════════ */}
        {step === 1 && (
          <div className="gc-card">
            <h3>Which cover do you want?</h3>
            {pcoveroptions === null && (
              <div className="gc-loading-inline" style={{ marginBottom: 16 }}>
                <FaSpinner className="gc-spinner-icon" />
                <span>Fetching cover options, please wait..</span>
              </div>
            )}
            <div className="gc-products-grid">
              {PRODUCTS.map((p) => (
                <div key={p.code} className="gc-product-card">
                  <img src={p.img} alt={p.name} className="gc-product-img" />
                  <button className="gc-product-name" onClick={() => selectProduct(p.code)}>
                    {p.name}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            STEP 2 — QUOTATION
           ══════════════════════════════════════ */}
        {step === 2 && (
          <div className="gc-card">
            <h3>Quotation Summary — {productName}</h3>
            <div style={{ display: 'flex', gap: 24, marginTop: 20 }}>
              {/* LEFT: Quotation Table - Flex 9 */}
              <div style={{ flex: 9 }}>
                <table className="gc-quote-table">
                  <thead>
                    <tr><th>Item</th><th style={{ textAlign: 'right' }}>Amount (KES)</th></tr>
                  </thead>
                  <tbody>
                   
                    <tr><td>Basic Premium</td><td>{formatter(premium)}</td></tr>
                    {premiumRate != null && <tr><td>Premium Rate</td><td>{formatter(premiumRate)}</td></tr>}
                    <tr><td>Net Premium</td><td>{formatter(netPremium)}</td></tr>
                    <tr><td>PHCF</td><td>{formatter(PHCF)}</td></tr>
                    <tr><td>Training Levy</td><td>{formatter(trainingLevy)}</td></tr>
                    <tr><td>Stamp Duty</td><td>{formatter(STAMP_DUTY)}</td></tr>
                    {benefitPremiumTotal > 0 && <tr><td>Additional Benefits</td><td>{formatter(benefitPremiumTotal)}</td></tr>}
                    <tr><td>Gross Premium</td><td>{formatter(basicPremium)}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* RIGHT: Additional Benefits - Flex 3 */}
              {additionalBenefits.length > 0 && (coverOption === 1 || coverOption === 58) && (
                <div className="gc-benefits-panel">
                  <h5 className="gc-benefits-title">Optional Additional Benefits</h5>
                  {productCode === 3617 && (
                    <p className="gc-benefits-note">
                      This premium is inclusive of Political, Violence and Terrorism (PVT) premium.
                    </p>
                  )}
                  <div className="gc-benefits-list">
                    {additionalBenefits.map((benefit) => {
                      const isSelected = selectedBenefits.some((b) => b.OID === benefit.OID);
                      const calculated = (benefit.MinimumRate * (sumInsured || 0)) / 100;
                      const benefitAmount = calculated < benefit.MinimumPremium ? benefit.MinimumPremium : calculated;

                      return (
                        <div key={benefit.OID} className={`gc-benefit-item ${isSelected ? 'selected' : ''}`}>
                          <label className="gc-benefit-label">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleBenefitChange(benefit)}
                              className="gc-benefit-checkbox"
                            />
                            <div className="gc-benefit-info">
                              <div className="gc-benefit-name">{benefit.ItemName}</div>
                              <div className="gc-benefit-amount">
                                +KES {formatter(benefitAmount)}
                              </div>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  {benefitPremiumTotal > 0 && (
                    <div className="gc-benefits-total">
                      <div className="gc-benefits-total-label">Additional Benefits</div>
                      <div className="gc-benefits-total-value">KES {formatter(benefitPremiumTotal)}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="gc-btn-row">
              <button className="gc-btn gc-btn-danger" onClick={() => setStep(1)}><FaArrowLeft /> Previous</button>
              <button className="gc-btn gc-btn-info" onClick={downloadQuotePDF}><FaDownload /> Download Quote</button>
              <button
                className={`gc-btn gc-btn-success ${((coverOption === 1 || coverOption === 58)) &&  coverPeriod== 'Annually' ? 'gc-btn-disabled-look' : ''}`}
                onClick={() => {
                  // if (coverOption === 1 || coverOption === 58) {
                  //   toast.warn('Unavailable at the moment');
                  //   return;
                  // }
                  setStep(3);
                }}
              >
                Buy Cover <FaArrowRight />
              </button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            STEP 3 — RISK DETAILS
           ══════════════════════════════════════ */}
        {step === 3 && (
          <div className="gc-card">
            <h3>Risk Details</h3>

            {/* Client search */}
            <div className="gc-form-section">
              <h4>Client Information</h4>
              <div className="gc-autocomplete">
                <div className="gc-form-group">
                  <label>Search Client by Name or ID *</label>
                  {clientsLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px', backgroundColor: '#f5f5f5', borderRadius: 4, color: '#666' }}>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      <span>Loading clients...</span>
                    </div>
                  ) : (
                    <input type="text" value={searchTerm} onChange={handleClientSearch} placeholder="Search by name or ID number" className={fieldClass(!!selectedClient)} />
                  )}
                </div>
                {!clientsLoading && suggestions.length > 0 && (
                  <ul className="gc-suggestions">
                    {suggestions.map((c) => (
                      <li key={c.OID} onClick={() => selectClient(c)}>{c.FirstName} {c.Surname} — {c.IdentificationNo}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="gc-new-client-btn" onClick={() => setShowClientModal(true)}><FaPlus /> New Client</button>

              {needsBranch && (
                <div className="gc-form-row" style={{ marginTop: 14 }}>
                  <div className="gc-form-group">
                    <label>Region *</label>
                    <select
                      value={selectedRegion}
                      onChange={(e) => { setSelectedRegion(e.target.value); setIntermediaryBranch(''); }}
                      className={fieldClass(!!selectedRegion)}
                    >
                      <option value="">Select Region</option>
                      {regionOptions.map((r) => (<option key={r} value={r}>{r}</option>))}
                    </select>
                  </div>
                  <div className="gc-form-group">
                    <label>Branch *</label>
                    <select
                      value={intermediaryBranch}
                      onChange={(e) => setIntermediaryBranch(e.target.value)}
                      disabled={!selectedRegion}
                      className={fieldClass(!!intermediaryBranch)}
                    >
                      <option value="">{selectedRegion ? 'Select Branch' : 'Select a Region first'}</option>
                      {branchesForRegion.map((b) => (<option key={b.OID} value={b.OID}>{b.BranchName}</option>))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="gc-form-section">
              <h4>Cover Period</h4>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Commencement Date *</label>
                  <input type="datetime-local" onChange={handleDateChange} className={fieldClass(!!cm)} min={(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}T${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`; })()} />
                </div>
                <div className="gc-form-group">
                  <label>Expiry Date</label>
                  <input type="text" value={ex || 'Expiry Date'} disabled className={fieldClass(!!ex)} />
                </div>
              </div>
            </div>

            {/* Vehicle */}
            <div className="gc-form-section">
              <h4>Vehicle Details</h4>

              <div className="gc-form-group" style={{ marginBottom: 14 }}>
                <label>Registration Number *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={reg}
                    onChange={(e) => setReg(e.target.value.replace(/\s+/g, '').toUpperCase())}
                    placeholder="e.g. KAA 000A"
                    className={fieldClass(!!reg)}
                    style={{ width: '30%' }}
                  />
                  <button
                    type="button"
                    className="gc-btn gc-btn-primary"
                    onClick={handleVehicleSearch}
                    disabled={vehicleSearchLoading || !reg}
                  >
                    {vehicleSearchLoading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSearch />} Search
                  </button>
                </div>
                {!vehicleSearchLoading && vehicleSearchStatus === 'success' && (
                  <span className="gc-field-success">Vehicle details found — please review the fields below.</span>
                )}
                {!vehicleSearchLoading && vehicleSearchStatus === 'failed' && (
                  <span className="gc-field-error">Could not find vehicle details automatically — please fill in the details manually.</span>
                )}
                {!vehicleSearchLoading && vehicleSearchStatus !== null && missingVehicleFields.length > 0 && (
                  <span className="gc-field-warning">Please fill in manually: {missingVehicleFields.join(', ')}</span>
                )}
              </div>

              {/* Double insurance status */}
              {doubleChecking && <div className="gc-double-status loading">Checking double insurance...</div>}
              {doubleStatus === true && <div className="gc-double-status danger">{doubleMessage}</div>}
              {doubleStatus === false && <div className="gc-double-status success">{doubleMessage}</div>}

              <div className="gc-form-row">
                <div className="gc-autocomplete gc-form-group">
                  <label>Vehicle Make *</label>
                  <input type="text" value={searchTerm2} onChange={handleMakeSearch} placeholder="Search make" className={vehicleFieldClass(makeCode)} />
                  {suggestions2.length > 0 && (
                    <ul className="gc-suggestions">{suggestions2.map((m) => (<li key={m.OID} onClick={() => selectMake(m)}>{m.Description}</li>))}</ul>
                  )}
                </div>
                <div className="gc-autocomplete gc-form-group">
                  <label>Vehicle Model *</label>
                  <input type="text" value={searchTerm3} onChange={handleModelSearch} placeholder="Search model" className={vehicleFieldClass(modelCode)} />
                  {suggestions3.length > 0 && (
                    <ul className="gc-suggestions">{suggestions3.map((m) => (<li key={m.OID} onClick={() => selectModel(m)}>{m.Description}</li>))}</ul>
                  )}
                </div>
              </div>

              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Chassis Number *</label>
                  <input type="text" value={chassis} onChange={(e) => setChassis(e.target.value)} className={vehicleFieldClass(chassis)} />
                </div>
                <div className="gc-form-group">
                  <label>Engine Number</label>
                  <input type="text" value={engineNumber} onChange={(e) => setEngineNumber(e.target.value)} className={vehicleFieldClass(engineNumber)} />
                </div>
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Engine Rating (CC)</label>
                  <input type="text" value={cc} onChange={(e) => setCC(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1500" className={vehicleFieldClass(cc)} />
                </div>
                <div className="gc-form-group">
                  <label>Color</label>
                  <input type="text" value={color} onChange={(e) => setColor(e.target.value)} className={vehicleFieldClass(color)} />
                </div>
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Year of Manufacture *</label>
                  <input type="number" value={yom} onChange={(e) => setYom(e.target.value)} className={vehicleFieldClass(yom)} />
                </div>
              </div>

              <div className="gc-form-group" style={{ marginBottom: 14 }}>
                <label>Log Book</label>
                <input type="file" accept="image/*,.pdf" onChange={handleLogbookUpload} />
              </div>
            </div>

            <div className="gc-btn-row">
              <button className="gc-btn gc-btn-danger" onClick={() => setStep(2)}><FaArrowLeft /> Previous</button>
              <button className="gc-btn gc-btn-success" onClick={openConfirmModal}>{confirmText}</button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            QUOTE MODAL (Cover options)
           ══════════════════════════════════════ */}
        {showQuoteModal && (
          <div className="gc-modal-overlay" onClick={() => setShowQuoteModal(false)}>
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>New Cover — {PRODUCTS.find((p) => p.code === Number(productCode))?.name}</h3>
                <button className="gc-modal-close" onClick={() => setShowQuoteModal(false)}><FaTimes /></button>
              </div>
             
              <div className="gc-modal-body">
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Cover Option</label>
                

                  {pcoveroptions === null ? (
                    <div className="gc-loading-inline">
                      <FaSpinner className="gc-spinner-icon" />
                      <span>Fetching cover options…</span>
                    </div>
                  ) : (
                    <select value={coverOptionCode} onChange={(e) => setCoverOptionCode(e.target.value)}>
                      <option value="" disabled>Select cover option</option>
                      {coverOptions.map((o) => (<option key={o.OID} value={o.OID}>{o.OptionName}</option>))}
                    </select>
                  )}
                </div>
                {(coverOption === 1 || coverOption === 58) && (
                  <div className="gc-form-group" style={{ marginBottom: 14 }}>
                    <label>Sum Insured</label>
                    <input type="number" value={sumInsured} onChange={(e) => setSumInsured(e.target.value)} placeholder="Sum insured" />
                  </div>
                )}
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Seating Capacity{[3582, 3583].includes(Number(productCode)) ? ' (max 7)' : ''}</label>
                  <input
                    type="number"
                    value={capacity}
                    placeholder="Seating capacity"
                    max={[3582, 3583].includes(Number(productCode)) ? 7 : undefined}
                    onChange={(e) => {
                      const val = e.target.value;
                      if ([3582, 3583].includes(Number(productCode)) && Number(val) > 7) {
                        toast.error('Capacity cannot exceed 7 for this product');
                        setCapacity(7);
                      } else {
                        setCapacity(val);
                      }
                    }}
                  />
                  {[3582, 3583].includes(Number(productCode)) && Number(capacity) > 7 && (
                    <span className="gc-field-error">Capacity cannot exceed 7 for Private Hire</span>
                  )}
                </div>
                {TONNAGE_PRODUCTS.includes(Number(productCode)) && (
                  <div className="gc-form-group" style={{ marginBottom: 14 }}>
                    <label>Tonnage (Load Capacity)</label>
                    <input type="text" value={tonnage} onChange={(e) => setTonnage(e.target.value)} />
                  </div>
                )}
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Payment Interval</label>
                  {coverOptionCode && coverOption === null ? (
                    <div className="gc-loading-inline">
                      <FaSpinner className="gc-spinner-icon" />
                      <span>Fetching payment intervals…</span>
                    </div>
                  ) : (
                    <select value={coverPeriod} onChange={(e) => setCoverPeriod(e.target.value)}>
                      <option value="" disabled>Number of Installments</option>
                      {paymentIntervals.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  )}
                </div>
                <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={getQuote} disabled={quoteText !== 'GET QUOTATION'}>
                  {quoteText}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            CONFIRM DETAILS MODAL
           ══════════════════════════════════════ */}
        {showConfirmModal && (
          <div className="gc-modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="gc-modal gc-modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Confirm Details</h3>
                <button className="gc-modal-close" onClick={() => setShowConfirmModal(false)}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                <div className="gc-confirm-section">
                  <h5>Insured Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Name:</strong> {clientFullname}</div>
                    <div className="gc-confirm-item"><strong>Phone:</strong> {clientPhone}</div>
                    <div className="gc-confirm-item"><strong>Email:</strong> {clientEmail}</div>
                    <div className="gc-confirm-item"><strong>KRA PIN:</strong> {clientPin}</div>
                  </div>
                </div>
                <div className="gc-confirm-section">
                  <h5>Vehicle Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Reg:</strong> <span style={{ color: '#e74c3c', fontWeight: 700 }}>{reg}</span></div>
                    <div className="gc-confirm-item"><strong>Make:</strong> {makeName}</div>
                    <div className="gc-confirm-item"><strong>Model:</strong> {modelName}</div>
                    <div className="gc-confirm-item"><strong>Year:</strong> {yom}</div>
                  </div>
                </div>
                <div className="gc-confirm-section">
                  <h5>Premium Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Product:</strong> {productName}</div>
                    <div className="gc-confirm-item"><strong>Interval:</strong> {coverPeriod}</div>
                    <div className="gc-confirm-item"><strong>Sum Insured:</strong> Kes. {formatter(sumInsured)}</div>
                    <div className="gc-confirm-item"><strong>Premium:</strong> <span style={{ color: '#27ae60', fontWeight: 700, fontSize: '1.1rem' }}>Kes. {formatter(basicPremium)}</span></div>
                  </div>
                </div>
                <div className="gc-btn-row" style={{ justifyContent: 'flex-end' }}>
                  <button className="gc-btn gc-btn-outline" onClick={() => setShowConfirmModal(false)}>Close</button>
                  <button className="gc-btn gc-btn-success" onClick={handleMakePayment} disabled={isPressed2}>
                    {isPressed2 ? 'Please wait...' : confirmPayText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            PAYMENT MODAL
           ══════════════════════════════════════ */}
        {showPayModal && (
          <div className="gc-modal-overlay">
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Make Payment</h3>
                {isCert === 0 && <button className="gc-modal-close" onClick={() => { setShowPayModal(false); setIsCert(0); setPrompt(false); setPayText('SEND REQUEST'); setWalletPaymentError(''); stopPaymentCountdown(); }}><FaTimes /></button>}
              </div>
              <div className="gc-modal-body">
                  <div className="gc-form-group" style={{ marginBottom: 16 }}>
                       <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Select Payment Method:</label>
                      <div style={{ display: 'flex', gap: 24 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={isMpesa && paymentMode === 0}
                            onChange={() => {
                              setIsMpesa(true);
                              setPaymentMode(0);
                            }}
                          />
                          Lipa na Mpesa
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={!isMpesa && paymentMode === 1}
                            onChange={() => {
                              setIsMpesa(false);
                              setPaymentMode(1);
                            }}
                          />
                          Pay from Wallet
                        </label>
                      </div> 
                    </div>
                {isCert === 0 && (
                  <>
                    {/* Payment Method Selection */}
                  
                    {/* End Payment Method Selection */}
                    <div className="gc-pay-amount">
                      <p>Amount to pay</p>
                      <h4>KES. {formatter(payAmount)}</h4>
                    </div>
                    {isMpesa && (
                      <div className="gc-form-group" style={{ marginBottom: 14 }}>
                        <label>Mpesa Number *</label>
                        <input type="text" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)} placeholder="e.g. 0712345678" />
                      </div>
                    )}
                    {paymentMode === 1 && <p style={{ fontSize: '0.85rem', color: '#555' }}>Available Wallet Balance: <strong>KES. {formatter(walletBalance)}</strong></p>}
                    {utilize === 1 && <p style={{ fontSize: '0.85rem', color: '#555' }}>Available Commission: <strong>KES. {availableCommission}</strong></p>}

                    {paymentMode === 0 &&
                      <>

                        {showPaymentTimer && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ position: 'relative', width: 70, height: 70 }}>
                              <svg width="70" height="70" viewBox="0 0 70 70" style={{ transform: 'scaleX(-1) rotate(-90deg)' }}>
                                <circle cx="35" cy="35" r={PAYMENT_TIMER_RADIUS} fill="none" stroke="#e5e5e5" strokeWidth="5" />
                                <circle
                                  cx="35" cy="35" r={PAYMENT_TIMER_RADIUS} fill="none" stroke="#1a5c5a" strokeWidth="5"
                                  strokeLinecap="round"
                                  strokeDasharray={PAYMENT_TIMER_CIRCUMFERENCE}
                                  strokeDashoffset={PAYMENT_TIMER_CIRCUMFERENCE * (1 - paymentCountdown / PAYMENT_TIMER_SECONDS)}
                                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                                />
                              </svg>
                              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 16, color: '#1a5c5a' }}>
                                {paymentCountdown}s
                              </span>
                            </div>
                            <span style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Waiting for M-Pesa confirmation…</span>
                          </div>
                        )}
                         {promptId === 0 && (
                      <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setPayText)} disabled={payText !== 'SEND REQUEST'}>
                        {payText}
                          </button>
                        )}
                        {promptId === 1 && (
                          <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setResendText)} disabled={resendText !== 'RESEND REQUEST'}>
                            {resendText}
                          </button>
                        )}
                      
                      </>
                    
                    }

                    {paymentMode === 1 &&

                      <>
                        {walletBalance < payAmount && <p style={{ color: '#c0392b', marginBottom: 12,fontSize:14 }}>Insufficient wallet balance. Please top up to proceed.</p>}
                        {walletPaymentError && <p style={{ color: '#c0392b', marginBottom: 12, fontSize: 14 }}>{walletPaymentError}</p>}
                        {walletBalance >= payAmount &&
                          <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setPayText)} disabled={payText !== 'SEND REQUEST'}>
                            {payText === 'SEND REQUEST' && walletPaymentError ? 'RETRY' : payText}
                          </button>
                      }

                      </>
                    }



                   

                    {isMpesa && !prompt && (
                      <>
                        <div className="gc-separator">─── OR ───</div>
                        <div className="gc-mpesa-steps">
                          <p>1. Go to MPESA Toolkit</p>
                          <p>2. Select Lipa na Mpesa</p>
                          <p>3. Select Paybill</p>
                          <p>4. Enter Paybill Number <strong>200300</strong></p>
                          <p>5. Enter Account <strong>{docNo}</strong></p>
                          <p>6. Amount to Pay <strong>KES. {payAmount}</strong></p>
                          <p>7. Enter MPESA PIN and Complete</p>
                          <button className="gc-already-paid" onClick={checkAutoApprovalAndPay}>Already paid? Click here</button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {isCert === 1 && (
                  <div className="gc-cert-success">
                    <a href={certURL} target="_blank" rel="noreferrer">DOWNLOAD CERTIFICATE</a>
                    <p style={{ fontSize: '0.82rem', color: '#888', marginTop: 10 }}>If certificate does not download, click the button again.</p>
                    <button className="gc-btn gc-btn-success" style={{ marginTop: 16 }} onClick={finish}>FINISH</button>
                  </div>
                )}

                {isCert === 2 && (
                  <div className="gc-cert-error">
                    <p>ERROR: {errorMessage}</p>
                    <p>
                      <a href="https://digital.definiteassurance.com/self-service" target="_blank" rel="noreferrer">
                        Issue Certificate from Self service
                      </a>
                    </p>
                    <button className="gc-btn gc-btn-success" onClick={retryCertificate}>{retryText}</button>
                    <br /><br />
                    <button className="gc-btn gc-btn-outline" onClick={finish}>FINISH</button>
                  </div>
                )}

                {isCert === 0 && (
                  <div className="gc-dmvic-errors">
                    <h6>Common DMVIC Errors</h6>
                    <p className="gc-error-title">Please note that the details entered do not match NTSA records</p>
                    <p className="gc-error-desc">The portal will generate certificate and send to your email despite this error. If not, navigate to 'Failed Certificates', search and click 'Retry'.</p>
                    <p className="gc-error-title">Invalid insured pin</p>
                    <p className="gc-error-desc">Navigate to 'Failed Certificates', click 'Edit', change KRA Pin, 'Save changes' and 'Retry'.</p>
                    <p className="gc-error-title">Invalid insured email</p>
                    <p className="gc-error-desc">Navigate to 'Failed Certificates', click 'Edit', change email, 'Save changes' and 'Retry'.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            VERIFY MPESA MODAL
           ══════════════════════════════════════ */}
        {showVerifyModal && (
          <div className="gc-modal-overlay">
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Confirm Payment</h3>
                <button className="gc-modal-close" onClick={() => { setShowVerifyModal(false); setCheckMpesa(false); }}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                {!checkMpesa && <p style={{ textAlign: 'center' }}>{checkPayment}</p>}
                {checkMpesa && (
                  <>
                    <p style={{ color: '#c0392b', marginBottom: 12 }}>Enter the M-Pesa transaction code below</p>
                    <div className="gc-form-group" style={{ marginBottom: 14 }}>
                      <label>Transaction Code</label>
                      <input type="text" value={mpesaCode} onChange={(e) => setMpesaCode(e.target.value)} placeholder="e.g. SBK1234ABC" />
                    </div>
                    <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={verifyMpesaCode} disabled={verifyText !== 'VERIFY'}>
                      {verifyText}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            NEW CLIENT MODAL
           ══════════════════════════════════════ */}
        {showClientModal && (
          <div className="gc-modal-overlay" onClick={() => setShowClientModal(false)}>
            <div className="gc-modal gc-modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>New Client</h3>
                <button className="gc-modal-close" onClick={() => setShowClientModal(false)}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>First Name *</label>
                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="gc-form-group">
                    <label>Last Name *</label>
                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>Other Names</label>
                    <input type="text" value={otherNames} onChange={(e) => setOtherNames(e.target.value)} />
                  </div>
                  <div className="gc-form-group">
                    <label>Phone Number *</label>
                    <input type="text" value={ncPhone} onChange={(e) => setNcPhone(e.target.value)} className={fieldClass(ncPhone.length >= 10 ? true : ncPhone.length > 0 ? false : null)} />
                    {ncPhone.length > 0 && ncPhone.length < 10 && <span className="gc-field-error">Min 10 digits</span>}
                  </div>
                </div>
                {`${firstName}${lastName}${otherNames}`.length > 50 && (
                  <span className="gc-field-error">Combined name length ({`${firstName}${lastName}${otherNames}`.length}/50) exceeds limit</span>
                )}
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>Email Address *</label>
                    <input type="text" value={ncEmail} onChange={(e) => {
                      const v = e.target.value;
                      if (!v) { setNcEmail(''); setNcEmailError(''); setNcEmailValid(true); return; }
                      const res = validateEmail(v);
                      setNcEmail(res.isValid ? res.sanitized : v);
                      setNcEmailError(res.isValid ? '' : res.message);
                      setNcEmailValid(res.isValid);
                    }} className={fieldClass(ncEmail ? ncEmailValid : null)} />
                    {ncEmailError && <span className="gc-field-error">{ncEmailError}</span>}
                  </div>
                  <div className="gc-form-group">
                    <label>Identification Type *</label>
                    <select value={idType} onChange={(e) => { setIdType(e.target.value); setIdNumber(''); }}>
                      <option value="" disabled>Select identification type</option>
                      <option value={ID_TYPES.NATIONAL_ID}>National ID</option>
                      <option value={ID_TYPES.PASSPORT}>Passport</option>
                      <option value={ID_TYPES.CERT_OF_INCORPORATION}>Certificate of Incorporation</option>
                    </select>
                  </div>
                </div>
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>{idNumberLabel} *</label>
                    <input type="text" value={idNumber} onChange={handleIdNumberChange} />
                  </div>
                  <div className="gc-form-group">
                    <label>KRA PIN *</label>
                    <input type="text" value={ncPin} maxLength={11} style={{ textTransform: 'uppercase' }}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase();
                        setNcPin(v);
                        if (v.length === 11) { const r = validateKRAPin(v); setNcPinError(r.isValid ? '' : r.message); setNcPinValid(r.isValid); }
                        else if (v.length > 0) { setNcPinError('Must be 11 characters'); setNcPinValid(false); }
                        else { setNcPinError(''); setNcPinValid(null); }
                      }}
                      className={fieldClass(ncPinValid)} />
                    {ncPinError && <span className="gc-field-error">{ncPinError}</span>}
                  </div>
                </div>

                <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 10 }}>Required Documents</h4>
                <div className="gc-upload-row">
                  <div className="gc-upload-item">
                    <label>Identification *</label>
                    <input type="file" onChange={(e) => setFile1(e.target.files[0])} />
                  </div>
                  <div className="gc-upload-item">
                    <label>KRA PIN *</label>
                    <input type="file" onChange={(e) => setFile3(e.target.files[0])} />
                  </div>
                  <div className="gc-upload-item">
                    <label>Any Other</label>
                    <input type="file" onChange={(e) => setFile2(e.target.files[0])} />
                  </div>
                </div>

                <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={createClient}
                  disabled={submitText !== 'SUBMIT' || !ncPinValid || (ncPhone.length > 0 && ncPhone.length < 10) || `${firstName}${lastName}${otherNames}`.length > 50 || !ncEmailValid || !idType || (idType === ID_TYPES.NATIONAL_ID && idNumber.length > 0 && !/^[0-9]+$/.test(idNumber))}>
                  {submitText}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
